<template>
  <sticky-header-layout
    v-loading="isPageLoading"
  >
    <template #header>
      <list-page-header>
        <div slot="left">
          <div
            class="d-flex mb-2"
            style="max-width: 300px;"
          >
            <el-input
              v-model="keywordsQuery"
              placeholder="Enter keywords"
              prefix-icon="el-icon-search"
              size="mini"
              clearable
            />
            <el-button
              :loading="isPageLoading || isLoading"
              class="ml-2"
              size="mini"
              type="primary"
              icon="el-icon-search"
              circle
              @click="handleSearch"
            />
          </div>
          <el-pagination
            slot="left"
            :disabled="isLoading"
            :current-page="listQuery.page"
            :page-sizes="listQuery.sizes"
            :page-size="listQuery.size"
            :total="listQuery.total"
            background
            layout="total, sizes, pager, jumper"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </div>
        <div
          slot="right"
          class="button-container"
        >
          <el-button
            class="add-button"
            type="primary"
            icon="el-icon-edit"
            @click="handleClickCreateButton"
          >
            {{ $t('button.create') }}
          </el-button>
        </div>
      </list-page-header>
    </template>

    <custom-table
      :loading="isLoading"
      :data="list"
      :column-list="columnList"
    >
      <template #columns="{ data, type }">
        <template v-if="isList(data)">
          <list-data-column
            :list="data"
            :type="type"
          />
        </template>
        <template v-else>
          {{ data }}
        </template>
      </template>

      <action-column
        slot="action-column"
        is-edit
        is-delete
        @edit="handleClickEditButton"
        @delete="handleClickDeleteButton"
      />
    </custom-table>
  </sticky-header-layout>
</template>

<script>
import { createComponent, onBeforeMount, ref } from '@vue/composition-api'

import ActionColumn from '../../components/table/ActionColumn.vue'
import CustomTable from '../../components/table/CustomTable.vue'
import ListDataColumn from '../../components/table/ListDataColumn.vue'
import ListPageHeader from '../layout/components/ListPageHeader.vue'

import EdmService from '../../api/edm'
import { columnList } from '../../const/edm'
import useTablePaging from '../../hooks/useTablePaging'
import { tableListHandler } from '../../utils/form'

const RegularEdmList = createComponent({
  name: 'RegularEdmList',
  components: {
    ActionColumn,
    CustomTable,
    ListDataColumn,
    ListPageHeader,
  },
  setup(props, { root }) {
    const { $router, $confirm } = root

    const keywordsQuery = ref('')
    const isPageLoading = ref(false)
    const {
      isLoading,
      list,
      listQuery,
      getList,
      handleSizeChange,
      handlePageChange,
      handleQueryParamsUpdate,
    } = useTablePaging({ service: EdmService.getRegularEdmList, listHandler: tableListHandler })

    onBeforeMount(() => {
      getList()
    })

    const deleteRegularEdm = async (id) => {
      isPageLoading.value = true
      try {
        await EdmService.deleteRegularEdm(id)
        handlePageChange(1)
        getList()
      } catch (error) {
        console.error(error)
      } finally {
        isPageLoading.value = false
      }
    }

    const handleClickCreateButton = () => {
      $router.push({ path: '/edm/create-regular-edm' })
    }

    const handleClickEditButton = ({ id }) => {
      $router.push({ path: `/edm/edit-regular-edm/${id}` })
    }
    
    const handleClickDeleteButton = ({ id }) => {
      $confirm(root.$t('message.confirmToDelete'), root.$t('common.warning'), {
        confirmButtonText: root.$t('button.delete'),
        cancelButtonText: root.$t('button.cancel'),
        confirmButtonClass: 'el-button--danger',
        type: 'warning',
      }).then(() => {
        deleteRegularEdm(id)
      })
    }
    
    const handleSearch = () => {
      handleQueryParamsUpdate({
        page: 1,
        keywords: keywordsQuery.value,
      })
      getList()
    }

    const isList = data => Array.isArray(data)

    return {
      columnList,
      isLoading,
      isPageLoading,
      keywordsQuery,
      list,
      listQuery,
      handleSizeChange,
      handlePageChange,
      handleClickCreateButton,
      handleClickEditButton,
      handleClickDeleteButton,
      handleSearch,
      isList,
    }
  },
})

export default RegularEdmList

</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/table.scss";

::v-deep {
  .el-tag {
    white-space: normal;
    height: auto;
    word-break: break-word;
  }
}
</style>
